import {
    useMemo,
    useState,
    useEffect
} from 'react';

import setColumnsForLot from './helpers/setColumnsForLot';
import setSuppliersForLot from './helpers/setSuppliersForLot';
import getFormattedSupplierResponses from './helpers/getFormattedSupplierResponses';
import getFormattedBuyerValues from './helpers/getFormattedBuyerValues';
import setTransformation from './helpers/setTransformation';
import { 
  prepareTopBids,
  prepareLotLevelFieldsBestBids,
  prepareLotLevelFieldsTopBids,
  prepareLSTLTPBestBids,
  updateBestBidsOfLineItems,
  updatePercentageDifferenceFromBestBid,
  updateLotLevelFieldsPercentageDifferenceFromBestBid,
  updateLSTLTPPercentageDifferenceFromBestBid
} from './helpers/getTopBids';
import getBestBidColumn from './helpers/getBestBidColumn';

const SR_NO_COLUMN = {
    "ucid": "sr_no",
    "term_name": "Sr. No.",
    "who_fills": "Buyer"
}

const useLot = ({
    localeRef,
    lotPricingSheetResponse,
    eventInfo,
    numberOfTopBidsToHighlight,
    lotLevelFields
  }) => {
    const initData = {
      buyerColumns: [],
      bidAction: null,
      supplierResponses: {},
      suppliersOrder: [],
      suppliers: {},
      normalSupplierColumns: [],
      alternateSupplierColumns: [],
      lineItems: [],
      responses: {},
      isTransformationAppliedAtLineItemLevel: false,
      totals_row: {},
      buyer_totals: {},
      surrogate_bidders_data: {},
      transformation_costs_data: {}
    };
  
    const [data, setData] = useState({
      ...initData
    });
  
    useEffect(() => {
      /**
       * Calling `setData` with the initial blank state
       * because when lot is changed, we want to remove all
       * the related info from the Response Analysis table state
       */
      setData({ ...initData });
      if (!Array.isArray(lotPricingSheetResponse?.data?.items?.column_data)) {
        return;
      }
      const response = JSON.parse(JSON.stringify(lotPricingSheetResponse));
      response.data.surrogate_bidders_data = Object.assign({},
        ...response.data.surrogate_bidders_data.map(key => ({[key['supplier_id']]: key}))
      );
      
      response.data.column_info = Object.assign({},
        ...response.data.items.column_data.map(key => ({[key['ucid']]: key}))
      );
      
      response.data.supplier_column_info = Object.assign(
        {}, 
        ...response.data.items.column_data.filter(
          (col)=> col.who_fills === 'Supplier'
      ).map(key => ({[key['ucid']]: key})));

      const formatValue = (value, decimalSpaces) => {
        const formatter = new Intl.NumberFormat(localeRef.current, {
          maximumFractionDigits: decimalSpaces,
          minimumFractionDigits: decimalSpaces
        });
        return formatter.format(value);
      };
      const bestBidColumnId = getBestBidColumn(response.data)
      response.data = setColumnsForLot(response.data, eventInfo)
      response.data = getFormattedSupplierResponses(response.data, formatValue)
      response.data = getFormattedBuyerValues(response.data, formatValue)
      response.data = setSuppliersForLot(response.data)
      response.data.isTransformationAppliedAtLineItemLevel = setTransformation(response.data)
      response.data.bestBids = prepareTopBids(
        response.data,
        eventInfo,
        numberOfTopBidsToHighlight,
        formatValue
      )
      
      // Calculate best bids for lot level fields
      const lotLevelFieldsBestBids = prepareLotLevelFieldsBestBids(
        response.data.supplier_responses,
        lotLevelFields,
        eventInfo,
        formatValue,
        bestBidColumnId,
        response.data.column_info
      )
      response.data.bestBids.set('lotLevelFieldsBestBids', lotLevelFieldsBestBids)
      
      // Calculate top bids for lot level fields (for ranking)
      const lotLevelFieldsTopBids = prepareLotLevelFieldsTopBids(
        response.data.supplier_responses,
        lotLevelFields,
        eventInfo,
        numberOfTopBidsToHighlight,
        formatValue,
        bestBidColumnId,
        response.data.column_info
      )
      response.data.bestBids.set('lotLevelFieldsTopBids', lotLevelFieldsTopBids)
      
      // Calculate best bids for LST and LTP
      const lstLtpBestBids = prepareLSTLTPBestBids(
        response.data.supplier_responses,
        eventInfo,
        formatValue,
        bestBidColumnId,
        response.data.column_info
      )
      response.data.bestBids.set('lstLtpBestBids', lstLtpBestBids)
      
      // Calculate percentage differences for lot level fields
      updateLotLevelFieldsPercentageDifferenceFromBestBid(
        response.data.supplier_responses,
        lotLevelFields,
        lotLevelFieldsBestBids,
        formatValue
      )
      
      // Calculate percentage differences for LST and LTP
      updateLSTLTPPercentageDifferenceFromBestBid(
        response.data.supplier_responses,
        lstLtpBestBids,
        formatValue,
        bestBidColumnId
      )
      
      updateBestBidsOfLineItems(response.data, formatValue)
      updatePercentageDifferenceFromBestBid(response.data, formatValue)
      const event = new CustomEvent('buyerTotals', {
        detail: response.data
      });
      window.dispatchEvent(event);

      const {
        column_data_for_buyer,
        column_info,
        compute_transformed_cost,
        compute_total_cost,
        suppliersOrder,
        suppliers,
        normalSupplierColumns,
        alternateSupplierColumns,
        items: {
          row_data: lineItems
        },
        supplier_responses: responses,
        totals_row,
        buyer_totals,
        surrogate_bidders_data,
        transformation_costs_data,
        bid_action: bidAction,
        responsesCountByRow,
        response_needed_for_all_line_items: responseNeededForAllLineItems,
        bestBids
      } = response.data;

      setData(data => ({
        ...data,
        bid_action: bidAction,
        bidAction,
        buyerColumns: [SR_NO_COLUMN, ...column_data_for_buyer],
        column_info,
        compute_total_cost,
        compute_transformed_cost,
        suppliersOrder,
        suppliers,
        normalSupplierColumns,
        alternateSupplierColumns,
        lineItems,
        responses,
        isTransformationAppliedAtLineItemLevel: response.data.isTransformationAppliedAtLineItemLevel,
        totals_row,
        buyer_totals,
        surrogate_bidders_data,
        transformation_costs_data,
        responsesCountByRow,
        responseNeededForAllLineItems,
        bestBids: response.data.bestBids,
        bestBidColumnId
      }));
    }, [
      lotPricingSheetResponse,
      localeRef,
      lotLevelFields
    ]);
  
    return [data, setData];
}

export default useLot;
